import React from 'react';
import { VStack, Flex, Text } from '@chakra-ui/react';
import { FiCalendar, FiChevronRight } from 'react-icons/fi';
import { stepTitleProps, wizardTokens } from '../styles';

// Turnaround = days from visit to required post. Options capped at the brand max. NO 14-day option
// (Figma comment). Labels are derived, not static — the brand's max is the recommended/default
// option and is badged at render time, so copy can never contradict the brand's actual setting.
export const TURNAROUND_OPTIONS: number[] = [1, 3, 5, 7];

/** Standard chips that fit under the cap, plus a chip at the cap itself so it is always pickable. */
export const getTurnaroundOptions = (cap: number): number[] => {
  const options = TURNAROUND_OPTIONS.filter((d) => d <= cap);
  return options.includes(cap) ? options : [...options, cap];
};

interface StepTurnaroundProps {
  maxTurnaroundDays: number;
  /** Go-live draft collabs: the capped draft due date ("Nov 7"), shown as the deadline instead of N days. */
  draftDueLabel?: string;
  selectedTurnaround: number | null;
  onSelectTurnaround: (days: number) => void;
  instructions?: string;
  onOpenInstructions?: () => void;
  remainingReschedulesText?: string | null;
  showCreditNote?: boolean;
  /** Delivery-only UGC (isDeliveryOnlyUgc): the content is delivered, never posted. */
  deliveryOnly?: boolean;
}

const DiscloseRow: React.FC<{ label: string; onClick?: () => void }> = ({ label, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    justify="space-between"
    w="100%"
    minH="52px"
    px="16px"
    borderRadius="16px"
    border="1px solid #DCDCD8"
    bg="#FFFFFF"
    _hover={{ bg: '#F2F2F0' }}
  >
    <Text fontSize="15px" fontWeight={600} color={wizardTokens.ink}>{label}</Text>
    <FiChevronRight size={20} color="#55555A" aria-hidden />
  </Flex>
);

const StepTurnaround: React.FC<StepTurnaroundProps> = ({
  maxTurnaroundDays,
  draftDueLabel,
  selectedTurnaround,
  onSelectTurnaround,
  instructions,
  onOpenInstructions,
  remainingReschedulesText,
  showCreditNote,
  deliveryOnly,
}) => {
  const verb = deliveryOnly ? 'deliver' : 'post';
  const options = getTurnaroundOptions(maxTurnaroundDays);

  return (
    <VStack spacing="18px" align="stretch" fontFamily="display">
      <Text {...stepTitleProps}>When will you {verb}?</Text>

      {/* Deadline card. The collab's locked turnaround (else the campaign's) is the commitment; the
          options below are only the creator's estimate against it. */}
      <Flex p="16px" borderRadius="18px" border="1px solid #E4E4E0" gap="12px" align="flex-start">
        <Flex flex="none" w="24px" h="24px" borderRadius="999px" bg={wizardTokens.ink} color="#C6F432" align="center" justify="center" aria-hidden>
          <FiCalendar size={13} strokeWidth={2.6} />
        </Flex>
        <Flex direction="column" gap="2px">
          <Text fontSize="16px" fontWeight={600} lineHeight="normal">Your deadline</Text>
          {draftDueLabel ? (
            <Text fontSize="14px" color="#55555A" lineHeight="1.4">
              Draft due by{' '}
              <Text as="span" color={wizardTokens.ink} fontWeight={600}>
                {draftDueLabel}
              </Text>
            </Text>
          ) : (
            <Text fontSize="14px" color="#55555A" lineHeight="1.4">
              You must {verb} within{' '}
              <Text as="span" color={wizardTokens.ink} fontWeight={600}>
                {maxTurnaroundDays} days
              </Text>{' '}
              after your visit.
            </Text>
          )}
        </Flex>
      </Flex>

      <Text fontSize="14px" lineHeight="1.45" color="#3E3E42">
        Your estimate for the brand — the deadline above still applies.
      </Text>

      <VStack role="radiogroup" aria-label="Your estimate" spacing="10px" align="stretch">
        {options.map((days) => {
          const isSelected = selectedTurnaround === days;
          return (
            <Flex
              as="button"
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-pressed={isSelected}
              key={days}
              onClick={() => onSelectTurnaround(days)}
              align="center"
              gap="14px"
              minH="72px"
              p="12px 16px"
              textAlign="left"
              borderRadius="16px"
              border={isSelected ? `2px solid ${wizardTokens.ink}` : '1px solid #DCDCD8'}
              bg={isSelected ? '#F1FADF' : '#FFFFFF'}
              color={wizardTokens.ink}
            >
              <Flex
                w="44px"
                h="44px"
                borderRadius="12px"
                align="center"
                justify="center"
                bg={isSelected ? wizardTokens.ink : '#F2F2F0'}
                color={isSelected ? '#C6F432' : wizardTokens.ink}
                textStyle="display"
                fontSize="22px"
                textTransform="none"
                flexShrink={0}
              >
                {days}d
              </Flex>
              <Flex direction="column" gap="2px" minW={0}>
                <Text fontSize="17px" fontWeight={600} lineHeight="normal">
                  {days}-day {deliveryOnly ? 'delivery' : 'post'}
                </Text>
                <Text fontSize="13px" color="#55555A" lineHeight="normal">
                  {deliveryOnly ? 'Deliver' : 'Post'} {days} day{days > 1 ? 's' : ''} after your visit
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </VStack>

      {(instructions || onOpenInstructions) && (
        <DiscloseRow label="Instructions" onClick={onOpenInstructions} />
      )}

      {remainingReschedulesText && (
        <Text fontSize="13px" lineHeight="1.45" color="#55555A">
          {remainingReschedulesText}
        </Text>
      )}
      {showCreditNote && (
        <Text fontSize="13px" lineHeight="1.45" color="#55555A">
          The brand handles your credit directly. Follow their credit instructions in the collab details, and message them in your inbox with any questions.
        </Text>
      )}
    </VStack>
  );
};

export default StepTurnaround;
